import { ReactNode } from 'react';

import useStyles from './EllipsisText.styles';

/**
 * Свойства компонента {@link EllipsisText}
 */
interface EllipsisTextProps {
  /**
   * Максимальное количество строк, которое текст должен занимать перед усечением многоточием.
   *
   * @defaultValue `2`
   */
  rows?: number;

  /**
   * Текстовое содержимое для отображения.
   */
  children: ReactNode;

  /**
   * Свойство word-break для контроля переноса строк.
   *
   * @defaultValue `'normal'`
   */
  wordBreak?: 'normal' | 'break-all' | 'keep-all' | 'break-word';

  /**
   * Если true, перенос слов будет осуществляться с использованием дефисов.
   *
   * @defaultValue `false`
   */
  hyphens?: boolean;

  /**
   * Максимальная ширина контейнера.
   */
  maxWidth?: number;
}

/**
 * Компонент для отображения текста с многоточием, если он переполняет заданное количество строк.
 *
 * @public
 * @param rows - Максимальное количество строк, которое текст должен занимать перед усечением многоточием.
 * @param wordBreak - Свойство word-break для контроля переноса строк.
 * @param children - Текстовое содержимое для отображения.
 * @param hyphens - Если true, перенос слов будет осуществляться с использованием дефисов.
 * @param maxWidth - Максимальная ширина контейнера.
 * @returns  JSX-элемент, представляющий текст с многоточием.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link EllipsisTextProps}
 */
export const EllipsisText = ({
  rows = 2,
  wordBreak = 'normal',
  children,
  hyphens = false,
  maxWidth,
}: EllipsisTextProps) => {
  const { classes } = useStyles({ rows, wordBreak, hyphens, maxWidth });

  return (
    <span
      className={`${classes.ellipsis} aqa_ellipsis_text`}
      data-testid="ellipsis-text-test-id"
    >
      {children}
    </span>
  );
};
